import { SITE_LOCALES } from "@shared/markets";

/**
 * i18n 設定
 * ---------------------------------------------------------------
 * 路由形式：/[locale]/...  例如 /en/jobs、/zh-TW/talent
 * 要新增語言：在 locales 加代碼 → 在 dictionaries/ 新增 <code>.json → 在 index.ts 註冊。
 */

/**
 * 介面語言。
 *
 * ── 名單怎麼從八種長到十八種 ────────────────────────────────
 *
 * 原本的依據是「哪些國家有自己的語言版本才有意義」，而那個
 * 判斷在只能手工翻譯的時候是對的：一種語言等於一千六百多條
 * 要有人寫、有人維護的字串，而半吊子的翻譯比誠實的英文糟。
 *
 * 加上建置期的機器翻譯之後，成本結構變了 —— 新增一種語言的
 * 邊際成本從「一個人幾天」降到「十四次 API 呼叫」。
 * 所以現在的限制不是翻譯，是**校對**。
 *
 * 規則因此改成：機器翻初稿 → 稽核擋掉佔位符壞掉與混語言 →
 * 上線前找母語使用者看過。名單外的國家仍然一律英文。
 *
 * ⚠ 十個新語系（it 之後的那些）目前是**未校對的機器翻譯**。
 * 已經發現的例子：義大利文的封鎖說明用了 "da lei"（陰性），
 * 等於假設被封鎖的人是女性。那種錯誤讀起來完全通順，
 * 只有懂那個語言的人看得出來。
 */
/*
  語言清單的唯一來源在 packages/shared —— 這裡只是重新匯出。

  分成兩份手寫清單的後果是安靜的漂移：shared 那一份決定
  「註冊時要不要存這個人選的語言」與「寄信用哪一種語言」，
  web 這一份決定「網址走不走得到」。兩邊差一個代碼，
  就會出現「畫面是法文、信是英文」而且沒有任何錯誤訊息。
*/
export const locales = SITE_LOCALES;

export type Locale = (typeof locales)[number];

export const defaultLocale: Locale = "en";

/** 語言選單一律用該語言自己的說法 —— 找得到自己的人才選得下去 */
export const localeNames: Record<Locale, string> = {
  en: "English",
  "zh-TW": "繁體中文",
  ja: "日本語",
  ko: "한국어",
  es: "Español",
  fr: "Français",
  de: "Deutsch",
  "pt-BR": "Português",
  it: "Italiano",
  nl: "Nederlands",
  pl: "Polski",
  tr: "Türkçe",
  id: "Bahasa Indonesia",
  th: "ไทย",
  vi: "Tiếng Việt",
  ru: "Русский",
  ar: "العربية",
  hi: "हिन्दी",
};

export const localeFlags: Record<Locale, string> = {
  en: "🇺🇸",
  "zh-TW": "🇹🇼",
  ja: "🇯🇵",
  ko: "🇰🇷",
  es: "🇪🇸",
  fr: "🇫🇷",
  de: "🇩🇪",
  "pt-BR": "🇧🇷",
  it: "🇮🇹",
  nl: "🇳🇱",
  pl: "🇵🇱",
  tr: "🇹🇷",
  id: "🇮🇩",
  th: "🇹🇭",
  vi: "🇻🇳",
  ru: "🇷🇺",
  /*
    阿拉伯文沒有單一國家。用沙烏地的旗子會冒犯其他二十幾個
    使用阿拉伯文的國家，而這一格本來就是「選語言」不是「選國家」。
  */
  ar: "🌐",
  hi: "🇮🇳",
};

/**
 * 由右往左書寫的語言。
 *
 * ── 誠實說目前支援到哪裡 ────────────────────────────────────
 *
 * dir="rtl" 讓**文字流向**正確：文字靠右、標點位置對、
 * 瀏覽器的選取與捲軸行為跟著翻。Tailwind 的 ms-/me-
 * （margin-inline-start/end）也會自動跟著走。
 *
 * 但**版面沒有完全鏡射** —— 寫死的 ml-/mr-、left-/right-、
 * pl-/pr- 不會跟著翻，所以側邊欄、箭頭圖示、某些間距
 * 會留在原來那一邊。
 *
 * 這一點寫下來，是因為「支援阿拉伯文」和「阿拉伯文版面是對的」
 * 是兩件事，而宣稱前者的時候很容易讓人以為包含後者。
 */
export const rtlLocales: readonly Locale[] = ["ar"];

export function isRtl(locale: string): boolean {
  return (rtlLocales as readonly string[]).includes(locale);
}

export function isLocale(value: string): value is Locale {
  return (locales as readonly string[]).includes(value);
}
